import { useState } from 'react'

/** Identity of a set of tracks — what a media element actually cares about. */
export function trackSetKey(tracks: readonly MediaStreamTrack[]): string {
  return tracks.map((t) => t.id).join('|')
}

/**
 * A MediaStream holding only `kind` tracks from `stream`, re-created only when
 * that set of tracks changes.
 *
 * A remote peer's stream carries audio and video together, and its identity
 * changes whenever either set does — the video consumer arriving after the
 * audio one, a microphone added mid-call, a producer re-pushed after a pipe
 * rebuild. An element bound to the combined stream re-binds on every one of
 * those, so the audio of someone whose camera just came on dropped out, and a
 * tile went blank because their audio changed. Binding each element to its own
 * kind makes it react to exactly the tracks it plays.
 *
 * Returns null when there are no tracks of that kind.
 */
export function useTrackStream(
  stream: MediaStream | null | undefined,
  kind: 'audio' | 'video',
): MediaStream | null {
  const tracks = stream ? (kind === 'audio' ? stream.getAudioTracks() : stream.getVideoTracks()) : []
  const key = trackSetKey(tracks)
  const [bound, setBound] = useState(() => ({ key, stream: key ? new MediaStream(tracks) : null }))

  // Derived state, updated during render: React re-runs this render with the
  // new value before committing, so no frame is ever painted with the old set.
  if (bound.key !== key) {
    const next = { key, stream: key ? new MediaStream(tracks) : null }
    setBound(next)
    return next.stream
  }
  return bound.stream
}
